import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Flame, Workflow, Cpu, Wrench, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, LineReveal, Kicker } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { IMAGES, PRODUCTS, PROCESS_STEPS, WHY_US, INDUSTRIES, COMPANY } from "../data/site";
import * as icons from "lucide-react";

const CAPABILITIES = [
    { label: "FURNACES", icon: Flame },
    { label: "PROCESS SYSTEMS", icon: Workflow },
    { label: "AUTOMATION", icon: Cpu },
    { label: "SERVICE", icon: Wrench },
];

const BENTO_SPANS = [
    "md:col-span-2 md:row-span-2",
    "", "", "md:col-span-2", "", "", "", "", "md:col-span-2",
];

const Home = () => {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section ref={heroRef} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-brand-ink grain" data-testid="hero-section">
                <motion.div className="absolute inset-0" style={{ y: bgY }}>
                    <img src={IMAGES.hero} alt="Industrial electric furnace in a heat treatment facility" className="h-[115%] w-full object-cover" />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/70 to-brand-ink/30" />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/80 via-transparent to-transparent" />
                <div className="technical-grid absolute inset-0" />

                <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-10 pb-0 pt-40">
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
                        <Kicker light>{COMPANY.name} — INDUSTRIAL FURNACE SOLUTIONS</Kicker>
                    </motion.div>
                    <h1 className="mt-6 font-display text-[13vw] sm:text-7xl lg:text-8xl font-extrabold uppercase leading-[0.95] tracking-tight text-white">
                        <LineReveal immediate delay={0.35}>Engineered for</LineReveal>
                        <LineReveal immediate delay={0.5}>
                            <span className="text-transparent [-webkit-text-stroke:1.5px_#F58220]">Extreme</span>{" "}
                            <span className="text-brand-ember">Possibilities</span>
                        </LineReveal>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.85, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
                    >
                        Industrial furnace solutions engineered for reliable heating, precise temperature control and demanding industrial processes.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-9 flex flex-wrap gap-4"
                    >
                        <Link to="/solutions" data-testid="hero-explore-solutions"
                            className="group flex items-center gap-3 bg-brand-red px-7 py-4 font-display text-sm font-bold tracking-[0.14em] text-white transition-colors duration-300 hover:bg-white hover:text-brand-ink">
                            EXPLORE SOLUTIONS
                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <Link to="/get-a-quote" data-testid="hero-request-quote"
                            className="group flex items-center gap-3 border border-white/30 px-7 py-4 font-display text-sm font-bold tracking-[0.14em] text-white backdrop-blur-sm transition-colors duration-300 hover:border-brand-ember hover:text-brand-ember">
                            REQUEST A QUOTE
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-16 grid grid-cols-2 border-t border-white/10 lg:grid-cols-4"
                        data-testid="hero-capabilities"
                    >
                        {CAPABILITIES.map((c, i) => (
                            <div key={c.label} className={`flex items-center gap-4 py-6 pr-4 ${i !== 0 ? "lg:border-l lg:border-white/10 lg:pl-8" : ""}`}>
                                <c.icon size={22} strokeWidth={1.5} className="shrink-0 text-brand-ember" />
                                <span className="font-display text-sm font-bold tracking-[0.18em] text-white">{c.label}</span>
                            </div>
                        ))}
                    </motion.div>
                </motion.div>
            </section>

            <Marquee />

            {/* BRAND INTRO */}
            <section className="bg-white py-20 md:py-28" data-testid="brand-intro">
                <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-10">
                    <div>
                        <Reveal><Kicker>WHO WE ARE</Kicker></Reveal>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase leading-[1.02] tracking-tight text-brand-charcoal">
                            <LineReveal>Engineering Heat.</LineReveal>
                            <LineReveal delay={0.12} className="text-brand-red">Powering Industry.</LineReveal>
                        </h2>
                        <Reveal delay={0.2}>
                            <p className="mt-7 text-base leading-relaxed text-slate-600">
                                SS Engineering Works is an industrial engineering and manufacturing company focused on electric industrial furnace solutions, process heating systems, electrical controls and automation.
                            </p>
                            <p className="mt-4 text-base leading-relaxed text-slate-600">
                                Our approach combines thermal engineering, electrical systems, process control and manufacturing to deliver dependable solutions for demanding industrial applications.
                            </p>
                            <Link to="/about" data-testid="intro-about-cta"
                                className="group mt-9 inline-flex items-center gap-3 border-b-2 border-brand-red pb-1 font-display text-sm font-bold tracking-[0.14em] text-brand-charcoal transition-colors hover:text-brand-red">
                                ABOUT SS ENGINEERING WORKS
                                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15} className="relative">
                        <div className="absolute -left-4 -top-4 h-full w-full border-2 border-brand-red" />
                        <div className="relative overflow-hidden">
                            <img src={IMAGES.chamber} alt="Glowing industrial furnace chamber" loading="lazy" className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105" />
                            <div className="absolute bottom-0 left-0 bg-brand-ink px-5 py-3">
                                <p className="font-mono text-[10px] tracking-[0.22em] text-brand-ember uppercase">{COMPANY.tagline}</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* PRODUCTS BENTO */}
            <section className="bg-brand-mist py-20 md:py-28 technical-grid-light" data-testid="home-products">
                <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <Reveal><Kicker>PRODUCT PORTFOLIO</Kicker></Reveal>
                            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-brand-charcoal">
                                <LineReveal>Furnace, Heating &</LineReveal>
                                <LineReveal delay={0.1}>Control Systems</LineReveal>
                            </h2>
                        </div>
                        <Reveal delay={0.15}>
                            <Link to="/products" data-testid="home-products-all"
                                className="group flex items-center gap-2 font-display text-sm font-bold tracking-[0.14em] text-brand-red">
                                VIEW ALL PRODUCTS <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                        {PRODUCTS.map((p, i) => (
                            <Reveal key={p.slug} delay={Math.min(i * 0.06, 0.4)} className={BENTO_SPANS[i]}>
                                <Link to={`/products/${p.slug}`} data-testid={`home-product-${p.slug}`}
                                    className="group relative block h-full min-h-[220px] overflow-hidden bg-brand-ink">
                                    <img src={p.image} alt={p.name} loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover opacity-50 transition-all duration-700 group-hover:scale-105 group-hover:opacity-70" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/30 to-transparent" />
                                    <div className="relative flex h-full flex-col justify-end p-6">
                                        <p className="font-mono text-[10px] tracking-[0.22em] text-brand-ember">{p.tag}</p>
                                        <p className="mt-2 font-display text-lg font-bold uppercase leading-tight text-white">{p.name}</p>
                                        <span className="mt-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] text-slate-400 opacity-0 transition-all duration-300 group-hover:opacity-100">
                                            VIEW DETAILS <ArrowRight size={12} />
                                        </span>
                                    </div>
                                    <span className="absolute left-0 top-0 h-1 w-0 bg-brand-red transition-all duration-500 group-hover:w-full" />
                                </Link>
                            </Reveal>
                        ))}
                        <Reveal delay={0.35} className="md:col-span-2">
                            <Link to="/get-a-quote" data-testid="home-product-custom"
                                className="group relative flex h-full min-h-[220px] flex-col justify-between overflow-hidden border-2 border-dashed border-slate-300 bg-white p-6 transition-colors duration-300 hover:border-brand-red">
                                <p className="font-mono text-[10px] tracking-[0.22em] text-brand-red">CUSTOM ENGINEERING</p>
                                <div>
                                    <p className="font-display text-lg font-bold uppercase leading-tight text-brand-charcoal">
                                        Need something not listed?
                                    </p>
                                    <span className="mt-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] text-slate-500 transition-colors group-hover:text-brand-red">
                                        TELL US YOUR PROCESS <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* PROCESS TIMELINE */}
            <section className="bg-brand-ink py-20 md:py-28 technical-grid grain relative" data-testid="home-process">
                <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <Reveal><Kicker light>ENGINEERING PROCESS</Kicker></Reveal>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white">
                        <LineReveal>From Requirement</LineReveal>
                        <LineReveal delay={0.1} className="text-brand-ember">To Performance.</LineReveal>
                    </h2>
                    <Reveal delay={0.2}>
                        <p className="mt-5 max-w-xl text-base text-slate-400">
                            A structured engineering approach from concept through commissioning and support.
                        </p>
                    </Reveal>
                </div>
                <div className="mt-14 overflow-x-auto pb-4 [scrollbar-width:thin]">
                    <div className="mx-auto flex w-max max-w-none gap-0 px-4 sm:px-6 lg:px-10">
                        {PROCESS_STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.07}>
                                <div className="group relative w-[240px] shrink-0 border-l border-brand-line px-6 py-8 transition-colors duration-300 hover:bg-brand-surface sm:w-[260px]" data-testid={`process-step-${s.n}`}>
                                    <p className="font-mono text-4xl font-semibold text-brand-line transition-colors duration-300 group-hover:text-brand-ember">{s.n}</p>
                                    <p className="mt-5 font-display text-base font-bold tracking-[0.16em] text-white">{s.title.toUpperCase()}</p>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                                    <span className="absolute left-0 top-0 h-0 w-[2px] bg-brand-red transition-all duration-500 group-hover:h-full" />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section className="bg-white py-20 md:py-28" data-testid="home-why">
                <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <Reveal><Kicker>WHY SS ENGINEERING WORKS</Kicker></Reveal>
                    <h2 className="mt-4 max-w-2xl font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-brand-charcoal">
                        <LineReveal>Built on Engineering.</LineReveal>
                        <LineReveal delay={0.1}>Proven in Operation.</LineReveal>
                    </h2>
                    <div className="mt-12 grid gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
                        {WHY_US.map((w, i) => {
                            const Icon = icons[w.icon];
                            return (
                                <Reveal key={w.title} delay={Math.min(i * 0.07, 0.35)}>
                                    <div className="group h-full bg-white p-8 transition-colors duration-300 hover:bg-brand-ink" data-testid={`why-${w.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                        <Icon size={26} strokeWidth={1.5} className="text-brand-red transition-colors duration-300 group-hover:text-brand-ember" />
                                        <p className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-brand-charcoal transition-colors duration-300 group-hover:text-white">{w.title}</p>
                                        <p className="mt-3 text-sm leading-relaxed text-slate-500 transition-colors duration-300 group-hover:text-slate-400">{w.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* INDUSTRIES STRIP */}
            <section className="border-y border-slate-200 bg-brand-mist py-20 md:py-24" data-testid="home-industries">
                <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <Reveal><Kicker>APPLICATIONS</Kicker></Reveal>
                            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-brand-charcoal">
                                <LineReveal>Across Demanding</LineReveal>
                                <LineReveal delay={0.1}>Industrial Environments</LineReveal>
                            </h2>
                        </div>
                        <Reveal delay={0.15}>
                            <Link to="/industries" data-testid="home-industries-all"
                                className="group flex items-center gap-2 font-display text-sm font-bold tracking-[0.14em] text-brand-red">
                                ALL INDUSTRIES <ArrowUpRight size={16} />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="mt-10 flex flex-wrap gap-3">
                        {INDUSTRIES.map((ind, i) => (
                            <Reveal key={ind.name} delay={Math.min(i * 0.04, 0.3)}>
                                <span className="inline-block border border-slate-300 bg-white px-5 py-2.5 font-display text-xs font-semibold tracking-[0.14em] text-brand-charcoal transition-colors duration-300 hover:border-brand-red hover:text-brand-red">
                                    {ind.name.toUpperCase()}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative overflow-hidden bg-brand-ink py-24 md:py-32 grain" data-testid="home-final-cta">
                <img src={IMAGES.chamber} alt="Glowing furnace heat" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-20" />
                <div className="absolute inset-0 bg-gradient-to-b from-brand-ink via-brand-ink/60 to-brand-ink" />
                <div className="relative mx-auto max-w-[1440px] px-4 text-center sm:px-6 lg:px-10">
                    <Reveal><Kicker light><span className="mx-auto">START YOUR PROJECT</span></Kicker></Reveal>
                    <h2 className="mx-auto mt-6 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[1.02] tracking-tight text-white">
                        <LineReveal>Ready to Engineer Your Next</LineReveal>
                        <LineReveal delay={0.12} className="text-brand-ember">Heating Solution?</LineReveal>
                    </h2>
                    <Reveal delay={0.2}>
                        <p className="mx-auto mt-6 max-w-xl text-base text-slate-400">
                            Tell us about your process. Our engineering team will help define the right furnace, heating and automation solution.
                        </p>
                        <div className="mt-10 flex flex-wrap justify-center gap-4">
                            <Link to="/get-a-quote" data-testid="final-cta-quote"
                                className="group flex items-center gap-3 bg-brand-red px-8 py-4 font-display text-sm font-bold tracking-[0.14em] text-white transition-colors duration-300 hover:bg-brand-ember">
                                REQUEST A QUOTE <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                            <Link to="/contact" data-testid="final-cta-contact"
                                className="flex items-center gap-3 border border-white/30 px-8 py-4 font-display text-sm font-bold tracking-[0.14em] text-white transition-colors duration-300 hover:border-brand-ember hover:text-brand-ember">
                                CONTACT ENGINEERING
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Home;
